import { useEffect } from "react";
import logoWithTagline from "@/ui/organisms/TopBar/Logo-with-tagline.svg";
import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import { Button, Checkbox } from "@proda-ai/ui-kit";
import type {
    TResponseGetThirdPartyDeals,
    TResponseGetThirdPartyDealsByDealIdOperatingStatement,
    TResponseGetThirdPartyDatabases,
    TResponseGetThirdPartyDealsByDealIdPeriods,
    TErrorGetThirdPartyDatabases,
    TErrorGetThirdPartyDeals,
    TErrorGetThirdPartyDealsByDealIdPeriods,
    TErrorGetThirdPartyDealsByDealIdOperatingStatement,
    TAllowedOperatingStatementPeriods,
    TResponseGetThirdPartyProjectsByProjectIdPeriods,
    TErrorGetThirdPartyProjectsByProjectIdPeriods,
    TResponseGetThirdPartyProjectsByProjectIdOperatingStatement,
    TErrorGetThirdPartyProjectsByProjectIdOperatingStatement,
    TResponseGetThirdPartyDealsByDealNameOperatingStatement,
    TErrorGetThirdPartyDealsByDealNameOperatingStatement,
    TResponseGetThirdPartyChartOfAccounts,
    TErrorGetThirdPartyChartOfAccounts,
    TResponseGetThirdPartyChartOfAccountsById,
    TErrorGetThirdPartyChartOfAccountsById,
} from "@/apis/apiThirdParty/apiThirdParty";

type responseStatus = "idle" | "pending" | "resolved" | "rejected";
import FullScreenAuthLoader from "@/ui/organisms/FullScreenAuthLoader";
import TextField from "@/ui/atoms/TextField";

export type ViewThirdPartyDocParams = {
    useState: () => {
        // Authentication
        isThirdPartyAuthenticated: boolean;
        token: string | null;
        tokenVisible: boolean;
        odinURL: string;
        openApiDocUrl: string;
        // API Request Parameters
        dealListRequestDatabaseIds: string | null;
        dealPeriodsRequestDealId: number | null;
        projectPeriodsRequestProjectId: number | null;
        operatingStatementRequestDealId: number | null;
        operatingStatementRequestProjectId: number | null;
        operatingStatementRequestFromDate: string | null;
        projectOperatingStatementRequestFromDate: string | null;
        projectOperatingStatementRequestToDate: string | null;
        operatingStatementRequestToDate: string | null;
        operatingStatementRequestAggregationInterval: TAllowedOperatingStatementPeriods | null;
        operatingStatementLookupType: "id" | "name";
        operatingStatementLookupValue: string | null;
        projectOperatingStatementRequestAggregationInterval: TAllowedOperatingStatementPeriods | null;

        // API Responses
        databaseListResponse: TResponseGetThirdPartyDatabases | null;
        dealListResponse: TResponseGetThirdPartyDeals | null;
        dealPeriodsResponse: TResponseGetThirdPartyDealsByDealIdPeriods | null;
        projectPeriodsResponse: TResponseGetThirdPartyProjectsByProjectIdPeriods | null;
        getOperatingStatementByIdResponse: TResponseGetThirdPartyDealsByDealIdOperatingStatement | null;
        getOperatingStatementByNameResponse: TResponseGetThirdPartyDealsByDealNameOperatingStatement | null;
        projectOperatingStatementResponse: TResponseGetThirdPartyProjectsByProjectIdOperatingStatement | null;

        // Response Statuses
        databaseListResponseStatus: responseStatus;
        dealListResponseStatus: responseStatus;
        dealPeriodsResponseStatus: responseStatus;
        projectPeriodsResponseStatus: responseStatus;
        getOperatingStatementByIdResponseStatus: responseStatus;
        getOperatingStatementByNameResponseStatus: responseStatus;
        projectOperatingStatementResponseStatus: responseStatus;

        // Errors
        databaseListRequestError: TErrorGetThirdPartyDatabases | null;
        dealListRequestError: TErrorGetThirdPartyDeals | null;
        dealPeriodsRequestError: TErrorGetThirdPartyDealsByDealIdPeriods | null;
        projectPeriodsRequestError: TErrorGetThirdPartyProjectsByProjectIdPeriods | null;
        getOperatingStatementByIdRequestError: TErrorGetThirdPartyDealsByDealIdOperatingStatement | null;
        getOperatingStatementByNameRequestError: TErrorGetThirdPartyDealsByDealNameOperatingStatement | null;
        projectOperatingStatementRequestError: TErrorGetThirdPartyProjectsByProjectIdOperatingStatement | null;

        // Chart of Accounts
        coaListResponse: TResponseGetThirdPartyChartOfAccounts | null;
        coaDetailResponse: TResponseGetThirdPartyChartOfAccountsById | null;
        coaListResponseStatus: responseStatus;
        coaDetailResponseStatus: responseStatus;
        coaListRequestError: TErrorGetThirdPartyChartOfAccounts | null;
        coaDetailRequestError: TErrorGetThirdPartyChartOfAccountsById | null;
        coaListRequestDatabaseIds: string | null;
        coaDetailRequestCoaId: number | null;

        // Other
        operatingStatementDetailed: boolean;
        projectOperatingStatementDetailed: boolean;
        isLoading: boolean;
        isNonProductionEnvironment: boolean;
    };
    useCases: () => {
        handleLoginButtonClicked: () => Promise<void>;
        handleLogoutButtonClicked: () => Promise<void>;
        handleRequestDatabases: () => Promise<void>;
        handleRequestDeals: () => Promise<void>;
        handleRequestProjectOperatingStatement: () => Promise<void>;
        handleRequestDealPeriods: () => Promise<void>;
        handleRequestOperatingStatementById: () => Promise<void>;
        handleRequestOperatingStatementByName: () => Promise<void>;
        handleRequestProjectPeriods: () => Promise<void>;
        toggleToken: () => Promise<void>;
        copyToken: () => Promise<void>;
        onMount: () => Promise<void>;
        setDealListRequestDatabaseIds: (databaseIds: string) => void;
        setDealPeriodsRequestDealId: (dealId: number) => void;
        setProjectPeriodsRequestProjectId: (projectId: number) => void;
        setOperatingStatementRequestDealId: (dealId: number) => void;
        setOperatingStatementRequestProjectId: (projectId: number) => void;
        resetDatabaseListResponse: () => void;
        resetDealListResponse: () => void;
        resetDealPeriodsResponse: () => void;
        resetOperatingStatementByIdResponse: () => void;
        resetOperatingStatementByNameResponse: () => void;
        resetProjectOperatingStatementResponse: () => void;
        resetProjectPeriodsResponse: () => void;
        setOperatingStatementDetailed: (detailed: boolean) => void;
        setProjectOperatingStatementDetailed: (detailed: boolean) => void;
        setOperatingStatementRequestFromDate: (fromDate: string) => void;
        setProjectOperatingStatementRequestFromDate: (fromDate: string) => void;
        setOperatingStatementRequestToDate: (toDate: string) => void;
        setProjectOperatingStatementRequestToDate: (toDate: string) => void;
        setOperatingStatementRequestAggregationInterval: (
            interval: TAllowedOperatingStatementPeriods,
        ) => void;
        setOperatingStatementLookupType: (type: "id" | "name") => void;
        setOperatingStatementLookupValue: (value: string) => void;
        handleRequestOperatingStatementByLookup: () => Promise<void>;
        setProjectOperatingStatementRequestAggregationInterval: (
            interval: TAllowedOperatingStatementPeriods,
        ) => void;
        handleRequestChartOfAccountsList: () => Promise<void>;
        handleRequestChartOfAccountsById: () => Promise<void>;
        setCoaListRequestDatabaseIds: (databaseIds: string) => void;
        setCoaDetailRequestCoaId: (coaId: number) => void;
        resetCoaListResponse: () => void;
        resetCoaDetailResponse: () => void;
    };
};

export const createViewThirdPartyDoc = ({
    useState,
    useCases,
}: ViewThirdPartyDocParams) => {
    const ViewThirdPartyDoc = () => {
        const {
            isThirdPartyAuthenticated,
            dealListRequestDatabaseIds,
            dealPeriodsRequestDealId,
            projectPeriodsRequestProjectId,
            operatingStatementRequestDealId,
            operatingStatementRequestProjectId,
            token,
            databaseListResponse,
            projectOperatingStatementResponse,
            databaseListResponseStatus,
            dealListResponse,
            dealListResponseStatus,
            projectOperatingStatementResponseStatus,
            odinURL,
            openApiDocUrl,
            dealPeriodsResponse,
            dealPeriodsResponseStatus,
            projectPeriodsResponse,
            projectPeriodsResponseStatus,
            getOperatingStatementByIdResponse,
            getOperatingStatementByIdResponseStatus,
            getOperatingStatementByNameResponse,
            getOperatingStatementByNameResponseStatus,
            operatingStatementDetailed,
            projectOperatingStatementDetailed,
            operatingStatementRequestFromDate,
            projectOperatingStatementRequestFromDate,
            operatingStatementRequestToDate,
            projectOperatingStatementRequestToDate,
            operatingStatementRequestAggregationInterval,
            projectOperatingStatementRequestAggregationInterval,
            operatingStatementLookupType,
            operatingStatementLookupValue,
            tokenVisible,
            databaseListRequestError,
            dealListRequestError,
            dealPeriodsRequestError,
            projectPeriodsRequestError,
            getOperatingStatementByIdRequestError,
            getOperatingStatementByNameRequestError,
            projectOperatingStatementRequestError,
            coaListResponse,
            coaDetailResponse,
            coaListResponseStatus,
            coaDetailResponseStatus,
            coaListRequestError,
            coaDetailRequestError,
            coaListRequestDatabaseIds,
            coaDetailRequestCoaId,
            isLoading,
            isNonProductionEnvironment,
        } = useState();
        const {
            handleLoginButtonClicked,
            handleLogoutButtonClicked,
            handleRequestDatabases,
            handleRequestDeals,
            handleRequestDealPeriods,
            handleRequestProjectPeriods,
            handleRequestProjectOperatingStatement,
            toggleToken,
            copyToken,
            onMount,
            setDealListRequestDatabaseIds,
            setDealPeriodsRequestDealId,
            setProjectPeriodsRequestProjectId,
            setOperatingStatementRequestProjectId,
            resetDatabaseListResponse,
            resetDealListResponse,
            resetDealPeriodsResponse,
            resetProjectPeriodsResponse,
            resetOperatingStatementByIdResponse,
            resetOperatingStatementByNameResponse,
            resetProjectOperatingStatementResponse,
            setOperatingStatementDetailed,
            setProjectOperatingStatementDetailed,
            setOperatingStatementRequestFromDate,
            setProjectOperatingStatementRequestFromDate,
            setOperatingStatementRequestToDate,
            setProjectOperatingStatementRequestToDate,
            setOperatingStatementRequestAggregationInterval,
            setProjectOperatingStatementRequestAggregationInterval,
            setOperatingStatementLookupType,
            setOperatingStatementLookupValue,
            handleRequestOperatingStatementByLookup,
            handleRequestChartOfAccountsList,
            handleRequestChartOfAccountsById,
            setCoaListRequestDatabaseIds,
            setCoaDetailRequestCoaId,
            resetCoaListResponse,
            resetCoaDetailResponse,
        } = useCases();
        useEffect(() => {
            void onMount();
        }, [onMount]);
        if (isLoading) {
            return <FullScreenAuthLoader label={"Authenticating..."} />;
        }
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                }}
            >
                <Box
                    component="header"
                    sx={{
                        backgroundColor: "var(--proda-navy-blue)",
                        padding: "1rem 20px",
                        display: "flex",
                        alignItems: "center",
                        height: "120px",
                        minHeight: "120px",
                        justifyContent: "space-between",
                    }}
                >
                    <a href="./docs">
                        <Box
                            component="img"
                            src={logoWithTagline}
                            alt="PRODA Logo"
                            sx={{
                                width: "250px",
                            }}
                        />
                    </a>
                    <Box
                        sx={{
                            color: "var(--proda-white)",
                            fontSize: "24px",
                            fontWeight: 400,
                            fontFamily: "DM Sans",
                        }}
                    >
                        Third Party API Docs
                    </Box>
                </Box>

                <Box
                    component="main"
                    sx={{
                        padding: "0.01em 16px",
                        backgroundColor: "#ccc",
                        flexGrow: 1,
                        display: "flex",
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: "var(--proda-white)",
                            marginLeft: "auto",
                            marginRight: "auto",
                            maxWidth: "1040px",
                            padding: "20px",
                            boxShadow: "0 0 15px rgba(0, 0, 0, 0.6)",
                            display: "flex",
                            flexDirection: "column",
                            flexGrow: 1,
                        }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: "2em",
                                display: "block",
                                my: "1rem",
                            }}
                        >
                            API Overview
                        </Typography>
                        <Box
                            component="p"
                            sx={{
                                fontWeight: 400,
                                fontSize: "14px",
                                fontFamily: "DM Sans",
                            }}
                        >
                            {`This guide is meant to show you how to access
                            the third party API. The API is meant to enable you to get operating statement data into your application.`}
                        </Box>
                        <Box
                            component="p"
                            sx={{
                                fontWeight: 400,
                                fontSize: "14px",
                                fontFamily: "DM Sans",
                                mt: "1rem",
                            }}
                        >
                            For a detailed reference of the API, including
                            request and response models, you can view the
                            complete{" "}
                            <a
                                href={openApiDocUrl}
                                target="_self"
                                rel="noopener noreferrer"
                                style={{
                                    color: "var(--proda-neon-blue)",
                                    textDecoration: "underline",
                                }}
                            >
                                OpenAPI documentation
                            </a>
                            .
                        </Box>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: "1.5em",
                                display: "block",
                                my: "30px",
                            }}
                        >
                            Try our API in this page
                        </Typography>

                        <Box
                            component="p"
                            sx={{
                                fontWeight: 400,
                                fontSize: "14px",
                                fontFamily: "DM Sans",
                            }}
                        >
                            {`You can experiment with our API in this very page if
                            you have a PRODA account. This relies on web-based
                            authentication, but you'll be handling the same
                            request/response formatting in either case.`}
                        </Box>
                        <Box
                            component="p"
                            sx={{
                                fontWeight: 400,
                                fontSize: "14px",
                                fontFamily: "DM Sans",
                            }}
                        >
                            {`To start, log in by clicking the button below.
                            You'll be redirected back to this page and be able to click buttons that use the API with your data from `}
                            <a
                                href={odinURL}
                                style={{
                                    color: "var(--proda-neon-blue)",
                                    textDecoration: "underline",
                                }}
                            >
                                {odinURL}
                            </a>{" "}
                            {`able to see your access token, which you
                            can use to access secured APIs.`}
                        </Box>
                        <Box
                            sx={{
                                marginTop: "20px",
                                display: "flex",
                                gap: "0.5rem",
                            }}
                        >
                            <Button
                                onClick={() => void handleLoginButtonClicked()}
                                pattern="primary"
                                isDisabled={isThirdPartyAuthenticated}
                                isInline
                                size="medium"
                            >
                                Log in
                            </Button>
                            <Button
                                onClick={() => void handleLogoutButtonClicked()}
                                pattern="primary"
                                isDisabled={!isThirdPartyAuthenticated}
                                isInline
                                size="medium"
                            >
                                Log out
                            </Button>
                        </Box>

                        {isThirdPartyAuthenticated && (
                            <>
                                <Box
                                    sx={{
                                        marginTop: "1rem",
                                    }}
                                >
                                    <Box
                                        component="p"
                                        sx={{
                                            fontWeight: "700",
                                            fontSize: "14px",
                                            fontFamily: "DM Sans",
                                        }}
                                    >
                                        {`You're logged in! Your access token is right
                                below. Be careful not to share it with others as it can be used to access your data!.`}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "0.5rem",
                                            mt: "1rem",
                                        }}
                                    >
                                        <Button
                                            onClick={() => void toggleToken()}
                                            pattern="secondary"
                                            isInline
                                            size="medium"
                                        >
                                            View/Hide Access Token
                                        </Button>
                                        <Button
                                            onClick={() => void copyToken()}
                                            pattern="secondary"
                                            isInline
                                            size="medium"
                                        >
                                            Copy Access Token
                                        </Button>
                                    </Box>

                                    <pre
                                        style={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-all",
                                            display: tokenVisible
                                                ? "block"
                                                : "none",
                                            width: "100%",
                                            textAlign: "left",
                                            border: "1px solid black",
                                            padding: "1rem",
                                            marginTop: "1rem",
                                        }}
                                    >
                                        {token}
                                    </pre>
                                </Box>
                                <Box
                                    component="p"
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: "14px",
                                        fontFamily: "DM Sans",
                                        mt: "1rem",
                                    }}
                                >
                                    {`Now try the buttons below to use the API with your
                            data. You can open your browser's network inspector
                            to view request URLs, headers and payloads.`}
                                </Box>
                                <Box
                                    component="section"
                                    sx={{
                                        margin: "2rem 0",
                                    }}
                                >
                                    <Typography variant="h3">
                                        Get Databases Endpoint
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            my: "1rem",
                                            display: "block",
                                            fontWeight: 400,
                                        }}
                                    >
                                        This uses the{" "}
                                        <strong>Get Databases</strong> endpoint
                                        to fetch databases accessible to the
                                        user.
                                    </Typography>
                                    <Button
                                        onClick={() => {
                                            if (
                                                databaseListResponseStatus ===
                                                "resolved"
                                            ) {
                                                void resetDatabaseListResponse();
                                            } else {
                                                void handleRequestDatabases();
                                            }
                                        }}
                                        isInline
                                        size="medium"
                                    >
                                        {databaseListResponseStatus ===
                                            "resolved" ||
                                        databaseListResponseStatus ===
                                            "rejected"
                                            ? "Reset"
                                            : "Request Your Databases"}
                                    </Button>
                                    <Box
                                        id="database-list-response"
                                        sx={{
                                            border: "1px solid black",
                                            padding: "1rem",
                                            marginTop: "1rem",
                                            width: "100%",
                                            textAlign: "left",
                                        }}
                                    >
                                        {databaseListResponseStatus ===
                                            "resolved" ||
                                        databaseListResponseStatus ===
                                            "rejected" ? (
                                            <pre
                                                style={{
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {JSON.stringify(
                                                    databaseListRequestError ??
                                                        databaseListResponse,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        ) : (
                                            "Press button to see a response"
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    component="section"
                                    sx={{
                                        margin: "2rem 0",
                                    }}
                                >
                                    <Typography variant="h3">
                                        Get Deals Endpoint{" "}
                                        <Box
                                            component="span"
                                            sx={{
                                                backgroundColor: "#ff6b6b",
                                                color: "white",
                                                padding: "2px 8px",
                                                borderRadius: "4px",
                                                fontSize: "0.7em",
                                                fontWeight: "bold",
                                                marginLeft: "8px",
                                            }}
                                        >
                                            DEPRECATED
                                        </Box>
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            my: "1rem",
                                            display: "block",
                                            fontWeight: 400,
                                        }}
                                    >
                                        This uses the{" "}
                                        <strong>Get Deals List</strong> endpoint
                                        to fetch deals accessible to the user.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "0.5rem",
                                            mt: "1rem",
                                        }}
                                    >
                                        <TextField
                                            name="dealListRequestDatabaseIds"
                                            type="text"
                                            value={
                                                dealListRequestDatabaseIds ?? ""
                                            }
                                            onChange={e =>
                                                setDealListRequestDatabaseIds(
                                                    String(e.target.value),
                                                )
                                            }
                                            placeholder="Database IDs (e.g. 1,2)"
                                        />
                                        <Button
                                            onClick={() => {
                                                void handleRequestDeals();
                                            }}
                                            isInline
                                            size="medium"
                                            disabled={
                                                dealListRequestDatabaseIds ===
                                                null
                                            }
                                        >
                                            Request Your Deals
                                        </Button>
                                        {dealListRequestDatabaseIds !==
                                            null && (
                                            <Button
                                                onClick={() => {
                                                    void resetDealListResponse();
                                                }}
                                                isInline
                                                size="medium"
                                            >
                                                Reset
                                            </Button>
                                        )}
                                    </Box>
                                    <Box
                                        id="deal-periods-response"
                                        sx={{
                                            border: "1px solid black",
                                            padding: "1rem",
                                            marginTop: "1rem",
                                            width: "100%",
                                            textAlign: "left",
                                        }}
                                    >
                                        {dealListResponseStatus ===
                                            "resolved" ||
                                        dealListResponseStatus ===
                                            "rejected" ? (
                                            <pre
                                                style={{
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {JSON.stringify(
                                                    dealListRequestError ??
                                                        dealListResponse,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        ) : (
                                            "Press button to see a response"
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    component="section"
                                    sx={{
                                        margin: "2rem 0",
                                    }}
                                >
                                    <Typography variant="h3">
                                        Get Deal Available Periods Endpoint
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            my: "1rem",
                                            display: "block",
                                            fontWeight: 400,
                                        }}
                                    >
                                        This uses the{" "}
                                        <strong>
                                            Get Deal Available Periods
                                        </strong>{" "}
                                        endpoint to fetch available periods for
                                        a deal.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "0.5rem",
                                            mt: "1rem",
                                        }}
                                    >
                                        <TextField
                                            name="dealPeriodsRequestDealId"
                                            type="text"
                                            value={
                                                dealPeriodsRequestDealId ?? ""
                                            }
                                            onChange={e =>
                                                setDealPeriodsRequestDealId(
                                                    Number(e.target.value),
                                                )
                                            }
                                            placeholder="Deal IDs (e.g. 1)"
                                        />
                                        <Button
                                            onClick={() => {
                                                void handleRequestDealPeriods();
                                            }}
                                            isInline
                                            size="medium"
                                            disabled={
                                                dealPeriodsRequestDealId ===
                                                null
                                            }
                                        >
                                            Request Your Deal Available Periods
                                        </Button>
                                        {dealPeriodsRequestDealId !== null && (
                                            <Button
                                                onClick={() => {
                                                    void resetDealPeriodsResponse();
                                                }}
                                                isInline
                                                size="medium"
                                            >
                                                Reset
                                            </Button>
                                        )}
                                    </Box>
                                    <Box
                                        id="deal-list-response"
                                        sx={{
                                            border: "1px solid black",
                                            padding: "1rem",
                                            marginTop: "1rem",
                                            width: "100%",
                                            textAlign: "left",
                                        }}
                                    >
                                        {dealPeriodsResponseStatus ===
                                            "resolved" ||
                                        dealPeriodsResponseStatus ===
                                            "rejected" ? (
                                            <pre
                                                style={{
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {JSON.stringify(
                                                    dealPeriodsRequestError ??
                                                        dealPeriodsResponse,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        ) : (
                                            "Press button to see a response"
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    component="section"
                                    sx={{
                                        margin: "1rem 0",
                                    }}
                                >
                                    <Typography variant="h3">
                                        Get Deal Operating Statement Endpoint
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            my: "1rem",
                                            display: "block",
                                            fontWeight: 400,
                                        }}
                                    >
                                        This uses either the{" "}
                                        <strong>
                                            Get Deal Operating Statement by ID
                                        </strong>{" "}
                                        or{" "}
                                        <strong>
                                            Get Deal Operating Statement by Name
                                        </strong>{" "}
                                        endpoint to fetch operating statements
                                        for a deal.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "0.5rem",
                                            mt: "1rem",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "grid",
                                                minWidth: "16rem",
                                                gap: "0.5rem",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    gap: "0.5rem",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        minWidth: "60px",
                                                    }}
                                                >
                                                    Lookup by:
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        width: "120px",
                                                    }}
                                                    component="select"
                                                    value={
                                                        operatingStatementLookupType
                                                    }
                                                    onChange={e => {
                                                        setOperatingStatementLookupType(
                                                            e.target.value as
                                                                | "id"
                                                                | "name",
                                                        );
                                                    }}
                                                >
                                                    <option value="id">
                                                        ID
                                                    </option>
                                                    <option value="name">
                                                        Name
                                                    </option>
                                                </Box>
                                            </Box>
                                            <TextField
                                                name="operatingStatementLookupValue"
                                                type="text"
                                                value={
                                                    operatingStatementLookupValue ??
                                                    ""
                                                }
                                                onChange={e =>
                                                    setOperatingStatementLookupValue(
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder={
                                                    operatingStatementLookupType ===
                                                    "id"
                                                        ? "Deal ID (e.g. 1)"
                                                        : "Deal Name (e.g. Evergreen Apartments)"
                                                }
                                            />

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: "70%",
                                                    }}
                                                >
                                                    Aggregation Interval*:
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                    }}
                                                    component="select"
                                                    value={
                                                        operatingStatementRequestAggregationInterval ??
                                                        1
                                                    }
                                                    onChange={e => {
                                                        setOperatingStatementRequestAggregationInterval(
                                                            Number(
                                                                e.target.value,
                                                            ) as TAllowedOperatingStatementPeriods,
                                                        );
                                                    }}
                                                >
                                                    <option value={1}>
                                                        Monthly
                                                    </option>
                                                    <option value={3}>
                                                        Quarterly
                                                    </option>
                                                    <option value={6}>
                                                        Half-Yearly
                                                    </option>
                                                    <option value={12}>
                                                        Yearly
                                                    </option>
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: "70%",
                                                    }}
                                                >
                                                    From Date*:
                                                </Typography>

                                                <TextField
                                                    sx={{
                                                        width: "100%",
                                                    }}
                                                    name="operatingStatementRequestFromDate"
                                                    type="date"
                                                    value={
                                                        operatingStatementRequestFromDate ??
                                                        ""
                                                    }
                                                    onChange={e =>
                                                        setOperatingStatementRequestFromDate(
                                                            String(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                    placeholder="From Date (e.g. 2024-01-01)"
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: "70%",
                                                    }}
                                                >
                                                    To Date*:
                                                </Typography>
                                                <TextField
                                                    sx={{
                                                        width: "100%",
                                                    }}
                                                    name="operatingStatementRequestToDate"
                                                    type="date"
                                                    value={
                                                        operatingStatementRequestToDate ??
                                                        ""
                                                    }
                                                    onChange={e =>
                                                        setOperatingStatementRequestToDate(
                                                            String(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                    placeholder="To Date (e.g. 2024-01-01)"
                                                />
                                            </Box>
                                            <Checkbox
                                                label="Detailed*"
                                                value={
                                                    operatingStatementRequestDealId?.toString() ??
                                                    ""
                                                }
                                                checked={
                                                    operatingStatementDetailed
                                                }
                                                onChange={() =>
                                                    setOperatingStatementDetailed(
                                                        !operatingStatementDetailed,
                                                    )
                                                }
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 400,
                                                    fontSize: "12px",
                                                    fontFamily: "DM Sans",
                                                }}
                                            >
                                                * = Optional
                                            </Typography>
                                        </Box>
                                        <Button
                                            onClick={() => {
                                                void handleRequestOperatingStatementByLookup();
                                            }}
                                            isInline
                                            size="medium"
                                            disabled={
                                                operatingStatementLookupValue ===
                                                    null ||
                                                operatingStatementLookupValue ===
                                                    ""
                                            }
                                        >
                                            Request Deal Operating Statement
                                        </Button>
                                        {operatingStatementLookupValue !==
                                            null && (
                                            <Button
                                                onClick={() => {
                                                    if (
                                                        operatingStatementLookupType ===
                                                        "id"
                                                    ) {
                                                        void resetOperatingStatementByIdResponse();
                                                    } else {
                                                        void resetOperatingStatementByNameResponse();
                                                    }
                                                }}
                                                isInline
                                                size="medium"
                                            >
                                                Reset
                                            </Button>
                                        )}
                                    </Box>
                                    <Box
                                        id="operating-statement-response"
                                        sx={{
                                            border: "1px solid black",
                                            padding: "1rem",
                                            marginTop: "1rem",
                                            width: "100%",
                                            textAlign: "left",
                                        }}
                                    >
                                        {(operatingStatementLookupType ===
                                            "id" &&
                                            (getOperatingStatementByIdResponseStatus ===
                                                "resolved" ||
                                                getOperatingStatementByIdResponseStatus ===
                                                    "rejected")) ||
                                        (operatingStatementLookupType ===
                                            "name" &&
                                            (getOperatingStatementByNameResponseStatus ===
                                                "resolved" ||
                                                getOperatingStatementByNameResponseStatus ===
                                                    "rejected")) ? (
                                            <pre
                                                style={{
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {JSON.stringify(
                                                    operatingStatementLookupType ===
                                                        "id"
                                                        ? getOperatingStatementByIdRequestError ??
                                                              getOperatingStatementByIdResponse
                                                        : getOperatingStatementByNameRequestError ??
                                                              getOperatingStatementByNameResponse,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        ) : (
                                            "Press button to see a response"
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    component="section"
                                    sx={{
                                        margin: "2rem 0",
                                    }}
                                >
                                    <Typography variant="h3">
                                        Get Project Available Periods Endpoint
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            my: "1rem",
                                            display: "block",
                                            fontWeight: 400,
                                        }}
                                    >
                                        This uses the{" "}
                                        <strong>
                                            Get Project Available Periods
                                        </strong>{" "}
                                        endpoint to fetch available periods for
                                        a project.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "0.5rem",
                                            mt: "1rem",
                                        }}
                                    >
                                        <TextField
                                            name="projectPeriodsRequestProjectId"
                                            type="text"
                                            value={
                                                projectPeriodsRequestProjectId ??
                                                ""
                                            }
                                            onChange={e =>
                                                setProjectPeriodsRequestProjectId(
                                                    Number(e.target.value),
                                                )
                                            }
                                            placeholder="Project IDs (e.g. 1)"
                                        />
                                        <Button
                                            onClick={() => {
                                                void handleRequestProjectPeriods();
                                            }}
                                            isInline
                                            size="medium"
                                            disabled={
                                                projectPeriodsRequestProjectId ===
                                                null
                                            }
                                        >
                                            Request Your Project Available
                                            Periods
                                        </Button>
                                        {projectPeriodsRequestProjectId !==
                                            null && (
                                            <Button
                                                onClick={() => {
                                                    void resetProjectPeriodsResponse();
                                                }}
                                                isInline
                                                size="medium"
                                            >
                                                Reset
                                            </Button>
                                        )}
                                    </Box>
                                    <Box
                                        id="project-periods-response"
                                        sx={{
                                            border: "1px solid black",
                                            padding: "1rem",
                                            marginTop: "1rem",
                                            width: "100%",
                                            textAlign: "left",
                                        }}
                                    >
                                        {projectPeriodsResponseStatus ===
                                            "resolved" ||
                                        projectPeriodsResponseStatus ===
                                            "rejected" ? (
                                            <pre
                                                style={{
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {JSON.stringify(
                                                    projectPeriodsRequestError ??
                                                        projectPeriodsResponse,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        ) : (
                                            "Press button to see a response"
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    component="section"
                                    sx={{
                                        margin: "1rem 0",
                                    }}
                                >
                                    <Typography variant="h3">
                                        Get Project Operating Statement Endpoint
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            my: "1rem",
                                            display: "block",
                                            fontWeight: 400,
                                        }}
                                    >
                                        This uses the{" "}
                                        <strong>
                                            Get Project Operating Statement
                                        </strong>{" "}
                                        endpoint to fetch operating statements
                                        for a project.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "0.5rem",
                                            mt: "1rem",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "grid",
                                                minWidth: "16rem",
                                                gap: "0.5rem",
                                            }}
                                        >
                                            <TextField
                                                name="operatingStatementRequestProjectId"
                                                type="number"
                                                value={
                                                    operatingStatementRequestProjectId ??
                                                    ""
                                                }
                                                onChange={e =>
                                                    setOperatingStatementRequestProjectId(
                                                        Number(e.target.value),
                                                    )
                                                }
                                                placeholder="Project ID (e.g. 1)"
                                            />

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: "70%",
                                                    }}
                                                >
                                                    Aggregation Interval*:
                                                </Typography>

                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                    }}
                                                    component="select"
                                                    value={
                                                        projectOperatingStatementRequestAggregationInterval ??
                                                        1
                                                    }
                                                    onChange={e => {
                                                        setProjectOperatingStatementRequestAggregationInterval(
                                                            Number(
                                                                e.target.value,
                                                            ) as TAllowedOperatingStatementPeriods,
                                                        );
                                                    }}
                                                >
                                                    <option value={1}>
                                                        Monthly
                                                    </option>
                                                    <option value={3}>
                                                        Quarterly
                                                    </option>
                                                    <option value={6}>
                                                        Half-Yearly
                                                    </option>
                                                    <option value={12}>
                                                        Yearly
                                                    </option>
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: "70%",
                                                    }}
                                                >
                                                    From Date*:
                                                </Typography>

                                                <TextField
                                                    sx={{
                                                        width: "100%",
                                                    }}
                                                    name="projectOperatingStatementRequestFromDate"
                                                    type="date"
                                                    value={
                                                        projectOperatingStatementRequestFromDate ??
                                                        ""
                                                    }
                                                    onChange={e =>
                                                        setProjectOperatingStatementRequestFromDate(
                                                            String(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                    placeholder="From Date (e.g. 2024-01-01)"
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: "70%",
                                                    }}
                                                >
                                                    To Date*:
                                                </Typography>
                                                <TextField
                                                    sx={{
                                                        width: "100%",
                                                    }}
                                                    name="projectOperatingStatementRequestToDate"
                                                    type="date"
                                                    value={
                                                        projectOperatingStatementRequestToDate ??
                                                        ""
                                                    }
                                                    onChange={e =>
                                                        setProjectOperatingStatementRequestToDate(
                                                            String(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                    placeholder="To Date (e.g. 2024-01-01)"
                                                />
                                            </Box>
                                            <Checkbox
                                                label="Detailed*"
                                                value={
                                                    operatingStatementRequestProjectId?.toString() ??
                                                    ""
                                                }
                                                checked={
                                                    projectOperatingStatementDetailed
                                                }
                                                onChange={() =>
                                                    setProjectOperatingStatementDetailed(
                                                        !projectOperatingStatementDetailed,
                                                    )
                                                }
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 400,
                                                    fontSize: "12px",
                                                    fontFamily: "DM Sans",
                                                }}
                                            >
                                                * = Optional
                                            </Typography>
                                        </Box>
                                        <Button
                                            onClick={() => {
                                                void handleRequestProjectOperatingStatement();
                                            }}
                                            isInline
                                            size="medium"
                                            disabled={
                                                operatingStatementRequestProjectId ===
                                                null
                                            }
                                        >
                                            Request Project Operating Statement
                                        </Button>
                                        {operatingStatementRequestProjectId !==
                                            null && (
                                            <Button
                                                onClick={() => {
                                                    void resetProjectOperatingStatementResponse();
                                                }}
                                                isInline
                                                size="medium"
                                            >
                                                Reset
                                            </Button>
                                        )}
                                    </Box>
                                    <Box
                                        id="operating-statement-response"
                                        sx={{
                                            border: "1px solid black",
                                            padding: "1rem",
                                            marginTop: "1rem",
                                            width: "100%",
                                            textAlign: "left",
                                        }}
                                    >
                                        {projectOperatingStatementResponseStatus ===
                                            "resolved" ||
                                        projectOperatingStatementResponseStatus ===
                                            "rejected" ? (
                                            <pre
                                                style={{
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                {JSON.stringify(
                                                    projectOperatingStatementRequestError ??
                                                        projectOperatingStatementResponse,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        ) : (
                                            "Press button to see a response"
                                        )}
                                    </Box>
                                </Box>
                                {isNonProductionEnvironment && (
                                    <>
                                        <Box
                                            component="section"
                                            sx={{
                                                margin: "2rem 0",
                                            }}
                                        >
                                            <Typography variant="h3">
                                                List Chart of Accounts Endpoint{" "}
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        backgroundColor:
                                                            "#f0ad4e",
                                                        color: "white",
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                        fontSize: "0.7em",
                                                        fontWeight: "bold",
                                                        marginLeft: "8px",
                                                    }}
                                                >
                                                    UNSTABLE
                                                </Box>
                                            </Typography>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    my: "1rem",
                                                    display: "block",
                                                    fontWeight: 400,
                                                }}
                                            >
                                                This uses the{" "}
                                                <strong>
                                                    List Chart of Accounts
                                                </strong>{" "}
                                                endpoint to fetch chart of
                                                accounts definitions accessible
                                                to the user. Optionally filter
                                                by database IDs.
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: "0.5rem",
                                                    mt: "1rem",
                                                }}
                                            >
                                                <TextField
                                                    name="coaListRequestDatabaseIds"
                                                    type="text"
                                                    value={
                                                        coaListRequestDatabaseIds ??
                                                        ""
                                                    }
                                                    onChange={e =>
                                                        setCoaListRequestDatabaseIds(
                                                            String(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                    placeholder="Database IDs (optional, e.g. 1,2)"
                                                />
                                                <Button
                                                    onClick={() => {
                                                        void handleRequestChartOfAccountsList();
                                                    }}
                                                    isInline
                                                    size="medium"
                                                >
                                                    Request Chart of Accounts
                                                </Button>
                                                {coaListRequestDatabaseIds !==
                                                    null && (
                                                    <Button
                                                        onClick={() => {
                                                            void resetCoaListResponse();
                                                        }}
                                                        isInline
                                                        size="medium"
                                                    >
                                                        Reset
                                                    </Button>
                                                )}
                                            </Box>
                                            <Box
                                                id="coa-list-response"
                                                sx={{
                                                    border: "1px solid black",
                                                    padding: "1rem",
                                                    marginTop: "1rem",
                                                    width: "100%",
                                                    textAlign: "left",
                                                }}
                                            >
                                                {coaListResponseStatus ===
                                                    "resolved" ||
                                                coaListResponseStatus ===
                                                    "rejected" ? (
                                                    <pre
                                                        style={{
                                                            whiteSpace:
                                                                "pre-wrap",
                                                        }}
                                                    >
                                                        {JSON.stringify(
                                                            coaListRequestError ??
                                                                coaListResponse,
                                                            null,
                                                            2,
                                                        )}
                                                    </pre>
                                                ) : (
                                                    "Press button to see a response"
                                                )}
                                            </Box>
                                        </Box>
                                        <Box
                                            component="section"
                                            sx={{
                                                margin: "2rem 0",
                                            }}
                                        >
                                            <Typography variant="h3">
                                                Get Chart of Accounts Details
                                                Endpoint{" "}
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        backgroundColor:
                                                            "#f0ad4e",
                                                        color: "white",
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                        fontSize: "0.7em",
                                                        fontWeight: "bold",
                                                        marginLeft: "8px",
                                                    }}
                                                >
                                                    UNSTABLE
                                                </Box>
                                            </Typography>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    my: "1rem",
                                                    display: "block",
                                                    fontWeight: 400,
                                                }}
                                            >
                                                This uses the{" "}
                                                <strong>
                                                    Get Chart of Accounts
                                                    Details
                                                </strong>{" "}
                                                endpoint to fetch account
                                                details for a specific chart of
                                                accounts by its ID.
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: "0.5rem",
                                                    mt: "1rem",
                                                }}
                                            >
                                                <TextField
                                                    name="coaDetailRequestCoaId"
                                                    type="text"
                                                    value={
                                                        coaDetailRequestCoaId ??
                                                        ""
                                                    }
                                                    onChange={e =>
                                                        setCoaDetailRequestCoaId(
                                                            Number(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                    placeholder="COA ID (e.g. 1)"
                                                />
                                                <Button
                                                    onClick={() => {
                                                        void handleRequestChartOfAccountsById();
                                                    }}
                                                    isInline
                                                    size="medium"
                                                    disabled={
                                                        coaDetailRequestCoaId ===
                                                        null
                                                    }
                                                >
                                                    Request COA Details
                                                </Button>
                                                {coaDetailRequestCoaId !==
                                                    null && (
                                                    <Button
                                                        onClick={() => {
                                                            void resetCoaDetailResponse();
                                                        }}
                                                        isInline
                                                        size="medium"
                                                    >
                                                        Reset
                                                    </Button>
                                                )}
                                            </Box>
                                            <Box
                                                id="coa-detail-response"
                                                sx={{
                                                    border: "1px solid black",
                                                    padding: "1rem",
                                                    marginTop: "1rem",
                                                    width: "100%",
                                                    textAlign: "left",
                                                }}
                                            >
                                                {coaDetailResponseStatus ===
                                                    "resolved" ||
                                                coaDetailResponseStatus ===
                                                    "rejected" ? (
                                                    <pre
                                                        style={{
                                                            whiteSpace:
                                                                "pre-wrap",
                                                        }}
                                                    >
                                                        {JSON.stringify(
                                                            coaDetailRequestError ??
                                                                coaDetailResponse,
                                                            null,
                                                            2,
                                                        )}
                                                    </pre>
                                                ) : (
                                                    "Press button to see a response"
                                                )}
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
                <Box
                    component="footer"
                    sx={{
                        backgroundColor: "var(--proda-navy-blue)",
                        padding: "1rem",
                        color: "white",
                        textAlign: "center",
                        bottom: 0,
                        width: "100%",
                    }}
                >
                    PRODA
                </Box>
            </Box>
        );
    };

    return ViewThirdPartyDoc;
};
